/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  convertMinutesToDisplayableTime,
  TIME_UNITS
} from 'common/utils/time.utils';
import { getAllTeamsGroupId, getManagementTool } from 'common/utils/utils';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { MAX_NUM_OF_SUMMARY_TYPES } from './SummaryItemBox';
import Dropdown from 'components/base/Dropdown';
import SearchInput from 'components/SearchInput';
import { getColor } from 'common/constants/_colors';
import { getAllIterationDurationPerIssueType } from './services/common';
import { getIterationsStats } from 'common/services/iterations.service';
import { useDebounceCallback } from '@react-hook/debounce';

import {
  PROVIDERS_MAP,
  SESSION_STORAGE_KEYS,
  SETTING_DEFINITION_LEVELS,
  METRICS
} from 'common/constants/constants';
import { isEqualAllTeams } from '../CustomReport/Filters/TeamsDropdown';
import { TEAMS_DEFAULT_ORDER_BY } from 'common/services/teams.service';
import { useSelector } from 'react-redux';
import { boardsProviderKeysSelector } from 'state/selectors/boards';
import { SettingValues as LinjsSettingValues } from '@linearb/linjs-common';
import TreeTeamsDropdown from 'components/teams/TreeTeamsDropdown/TreeTeamsDropdown';
import useSettingsService from 'components/hooks/useSettingsService';
import { getSensorAuthorizations } from 'state/selectors/sensors-authorizations';
import { ALL_TEAMS } from 'common/constants/teams';
import { teamsSelector } from 'state/selectors/teams';

// eslint-disable-next-line max-lines-per-function
const usePMReport = ({
  selectedSprintName,
  onSprintSelection,
  service,
  currentTeams,
  filters,
  onBoardSelection,
  selectedBoardId
}) => {
  const {
    formatData,
    convertMinutesToTotalDisplay,
    getChartConfig,
    getTableConfig,
    getTableData,
    getChartBarsDef,
    getDynamicStates,
    getFilterByIssuesParams,
    getFilterByIterationsParam,
    getApiAdditionalFilters
  } = service;
  const sensorAuthorizations = useSelector(getSensorAuthorizations);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState('all');
  const [selectedEntities, setSelectedEntities] = useState(
    selectedBoardId || 'all'
  );
  const [iterationsNumber, setIterationsNumber] = useState(5);
  const [totalDurationPerIssueType, setTotalDurationPerIssueType] = useState(
    {}
  );
  const [states, setStates] = useState({
    dynamicStates: [],
    chartBarsDef: [],
    loaded: false
  });
  const [iterations, setIterations] = useState([]);
  const [filteredIterations, setFilteredIterations] = useState([]);

  const teams = useSelector(teamsSelector) || [];
  const allTeamsGroupId = getAllTeamsGroupId(teams);

  const allIterationsDurationPerIssueType =
    getAllIterationDurationPerIssueType(filteredIterations);

  const pmSensor = getManagementTool({ sensorAuthorizations }) || {};
  const pmSensorName = _.get(pmSensor, 'sensor_name', '');
  const pmConfig = PROVIDERS_MAP[pmSensorName.toLowerCase()] || {};
  const { getSettingOrDefinitionValue } = useSettingsService();
  const orgEntitiesSetting =
    getSettingOrDefinitionValue({
      name: LinjsSettingValues.PM_IMPORT_STRATEGY,
      definitionLevel: 'organization'
    }) || {};
  const settingNumOfIterations = getSettingOrDefinitionValue(
    {
      name: LinjsSettingValues.INVESTMENT_PROFILE_ITERATIONS,
      definitionLevel: SETTING_DEFINITION_LEVELS.organization
    },
    METRICS.TIME_FRAME_FILTER_OPTIONS
  );
  const numOfIterations = Math.max(...settingNumOfIterations);
  const orgEntities = orgEntitiesSetting[pmConfig.entities_settings_key] || [];

  const boardsProviderKeys = useSelector(boardsProviderKeysSelector);

  const isAllTeamsSelectedGlobally = isEqualAllTeams(
    filters.defaultSelectedTeam
  );

  const loadFromAPI = useDebounceCallback(async () => {
    setApiLoaded(false);
    setStates({
      dynamicStates: [],
      chartBarsDef: [],
      loaded: false
    });

    const isTeamAllTeamsGroup =
      filters.firstGloballySelectedTeamId &&
      filters.firstGloballySelectedTeamId === allTeamsGroupId;

    let apiFilters = {};
    if (
      !isEqualAllTeams(filters.globallySelectedTeams) &&
      !isTeamAllTeamsGroup
    ) {
      apiFilters = {
        filter_by: 'team_id',
        entity_id: filters.firstGloballySelectedTeamId
      };
    } else if (selectedEntities !== 'all') {
      apiFilters = { filter_by: 'container_id', entity_id: selectedEntities };
    }
    const data = await getIterationsStats({
      ...apiFilters,
      ...getApiAdditionalFilters(),
      ...(numOfIterations ? { limit: numOfIterations } : {})
    });
    setIterations(formatData(data));
    setApiLoaded(true);
  }, 200);

  useEffect(() => {
    loadFromAPI();
  }, [filters.globallySelectedTeams]);

  useEffect(() => {
    isEqualAllTeams(filters.globallySelectedTeams) && loadFromAPI();
  }, [selectedEntities]);

  useEffect(() => {
    setFilteredIterations(
      _.takeRight(iterations, iterationsNumber).filter(({ iterationName }) =>
        iterationName
          .toLowerCase()
          .includes((selectedSprintName || '').toLowerCase())
      )
    );
  }, [iterations, selectedSprintName, iterationsNumber]);

  useEffect(() => {
    generateStates(filteredIterations);
  }, [filteredIterations]);

  const createFilters = ({ teamsOrderBy = TEAMS_DEFAULT_ORDER_BY } = {}) => {
    const entitiesDropdownOptions = [
      { value: 'all', displayValue: `All ${pmConfig.entity_name_plural}` },
      ...orgEntities.map(({ provider_id }) => {
        const entity = _.get(boardsProviderKeys, provider_id);
        return {
          value: provider_id,
          displayValue: _.get(entity, 'name')
        };
      })
    ];

    const getSelectedTeamsIds = () => {
      const globallySelectedTeams = filters.globallySelectedTeams || [];
      const isCustomAllSelected =
        _.get(globallySelectedTeams, '[0]') === ALL_TEAMS.id;

      if (isCustomAllSelected) {
        return [ALL_TEAMS.id];
      }
      return [_.get(globallySelectedTeams, '[0].id')];
    };

    return [
      <TreeTeamsDropdown
        key='TreeTeamsDropdown'
        selectedTeamIds={getSelectedTeamsIds()}
        onChange={selectedTeams => {
          filters.setGloballySelectedTeams(selectedTeams);
          filters.setSelectedContributor(null);
          const filtersSnapshot = {
            globallySelectedTeams: selectedTeams,
            selectedRepo: [],
            selectedService: [],
            selectedContributor: null
          };
          BrowserStorageAccessor().session.set(
            SESSION_STORAGE_KEYS.METRICS_FILTERS,
            JSON.stringify(filtersSnapshot)
          );
        }}
        width='auto'
        menuWidth='25rem'
        menuWidthExpanded='50rem'
        numOfMenus={2}
        customAllItem={ALL_TEAMS}
        isFilterTreeBySelectedTeam={!isAllTeamsSelectedGlobally}
      />,
      isEqualAllTeams(filters.globallySelectedTeams) && orgEntities.length ? (
        <Dropdown
          menuHeight='30rem'
          width='18rem'
          height='2.1rem'
          key='dropdownEntities'
          options={entitiesDropdownOptions}
          selectedValue={selectedEntities}
          onChange={value => {
            setSelectedEntities(value);
            onBoardSelection(value);
          }}
          placeholder={`All ${pmConfig.entity_name_plural}`}
        />
      ) : null,
      <Dropdown
        menuHeight='30rem'
        width='18rem'
        height='2.1rem'
        key='dropdownIssues'
        selectedValue={selectedIssues}
        onChange={value => setSelectedIssues(value)}
        {...getFilterByIssuesParams(states.chartBarsDef)}
      />,
      <Dropdown
        menuHeight='30rem'
        width='18rem'
        height='2.1rem'
        key='dropdownIterations'
        selectedValue={iterationsNumber}
        onChange={value => {
          setIterationsNumber(value);
        }}
        {...getFilterByIterationsParam()}
      />,
      <SearchInput
        height='3rem'
        width='18rem'
        key='inputFilter'
        placeholder='Search iteration name'
        value={selectedSprintName}
        onChange={val => onSprintSelection(val)}
      />
    ];
  };

  const generateStates = iterations => {
    const chartBarsDef = getChartBarsDef(iterations);
    setStates({
      ...states,
      dynamicStates: getDynamicStates(iterations),
      chartBarsDef,
      loaded: true
    });
    setTotalDurationPerIssueType(allIterationsDurationPerIssueType);
  };

  const chartConfig = getChartConfig(
    iterations,
    selectedIssues,
    states.chartBarsDef
  );
  const tableConfig = getTableConfig(
    totalDurationPerIssueType,
    states.chartBarsDef
  );
  const tableData = getTableData(filteredIterations);
  const moreTotal = _.sum(
    tableConfig
      .filter(({ isMoreIssueType }) => isMoreIssueType)
      .map(i => i.total)
  );
  const moreTotalPercents = _.sum(
    tableConfig
      .filter(({ isMoreIssueType }) => isMoreIssueType)
      .map(i => +i.percents)
  );
  const convertedMoreTotalDays = convertMinutesToTotalDisplay(
    convertMinutesToDisplayableTime({
      minutes: moreTotal,
      maxTimeUnit: TIME_UNITS.days
    })
  );
  const numOfTypes = tableConfig.filter(
    ({ dataPoint }) => dataPoint !== 'name'
  ).length;
  const numOfMore = numOfTypes - MAX_NUM_OF_SUMMARY_TYPES;

  const pieData = tableConfig
    .filter(({ dataPoint }) => dataPoint !== 'name')
    .map(({ key, convertedTotalDays, percents, color }) => ({
      name: key,
      value: _.round(percents, 1),
      total: convertedTotalDays,
      color: getColor(color)
    }));

  return {
    iterations: filteredIterations,
    pieData,
    tableConfig,
    numOfTypes,
    numOfMore: numOfMore > 0 ? numOfMore : 0,
    moreTotalPercents,
    convertedMoreTotalDays,
    chartConfig,
    tableData,
    chartBarsDef: states.chartBarsDef,
    loaded: apiLoaded && states.loaded,
    createFilters
  };
};

export default usePMReport;
